import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useToast } from '../contexts/ToastContext';
import { track } from '../utils/analytics';
import { errMsg } from '../redux/utils/errMsg';
import './SendToAppModal.scss';

const LABELS = { rekordbox: 'Rekordbox', serato: 'Serato', traktor: 'Traktor', virtualdj: 'Virtual DJ' };

/**
 * "Send to <DJ app>" picker. Lists every MixGoat playlist with what will
 * actually land in the app (local files only); pre-checks the ones not
 * already in that app. Sending a playlist that IS already there updates it
 * in place. `onExportXml` (Rekordbox only) keeps the old file export as a
 * secondary path.
 */
export default function SendToAppModal({ app, show, onClose, onExportXml, onSent }) {
    const toast = useToast();
    const label = LABELS[app] || app;
    const [rows, setRows] = useState(null);
    const [checked, setChecked] = useState(new Set());
    const [sending, setSending] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!show) return;
        setRows(null); setQuery('');
        window.electron.djapp.previewSend(app).then((list) => {
            const sorted = [...(list || [])].sort((a, b) => (a.linkedToApp === b.linkedToApp ? a.name.localeCompare(b.name) : a.linkedToApp ? 1 : -1));
            setRows(sorted);
            // Default: everything that isn't in the app yet and has at least one local file.
            setChecked(new Set(sorted.filter(r => !r.linkedToApp && r.local > 0).map(r => r.id)));
        }).catch(() => { setRows([]); });
    }, [show, app]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (rows || []).filter(r => !q || r.name.toLowerCase().includes(q));
    }, [rows, query]);

    const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const setAll = (on) => setChecked(on ? new Set(visible.filter(r => r.local > 0).map(r => r.id)) : new Set());

    const selected = (rows || []).filter(r => checked.has(r.id));
    const totals = selected.reduce((t, r) => ({ tracks: t.tracks + r.local, streaming: t.streaming + r.streaming, missing: t.missing + r.missing }), { tracks: 0, streaming: 0, missing: 0 });

    const send = async () => {
        if (!selected.length) return;
        setSending(true);
        try {
            const res = await window.electron.djapp.sendPlaylists(app, selected.map(r => r.id));
            if (res?.error) throw new Error(res.error);
            const t = res.totals;
            track('playlists_sent_to_dj_app', { app, playlist_count: t.playlists, track_count: t.tracks, skipped_streaming: t.streaming, skipped_missing: t.missing });
            const skipped = [];
            if (t.streaming) skipped.push(`${t.streaming} streaming-only`);
            if (t.missing) skipped.push(`${t.missing} not on this device`);
            toast.success(`Sent ${t.playlists} playlist${t.playlists === 1 ? '' : 's'} (${t.tracks} track${t.tracks === 1 ? '' : 's'}) to ${label}${skipped.length ? ` — skipped ${skipped.join(', ')}` : ''}`, 7000);
            onSent?.(res);
            onClose();
        } catch (err) {
            toast.error(`Send to ${label} failed: ${errMsg(err)}`, 9000);
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onHide={sending ? undefined : onClose} centered className="send-to-app-modal" size="lg">
            <Modal.Header closeButton={!sending}>
                <Modal.Title>Send playlists to {label}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="send-hint">
                    Writes the selected playlists into your {label} library. Playlists already in {label} are updated in place.
                    Only tracks with a file on this computer are sent.
                    {app === 'rekordbox' && <> Rekordbox must be closed.</>}
                </p>
                <div className="send-toolbar">
                    <input className="send-search" placeholder="Filter playlists…" value={query} onChange={(e) => setQuery(e.target.value)} />
                    <button type="button" className="send-link" onClick={() => setAll(true)}>Select all</button>
                    <button type="button" className="send-link" onClick={() => setAll(false)}>None</button>
                </div>
                {rows === null ? (
                    // Skeleton sized like the loaded list, so the modal opens
                    // at (near) final height instead of growing when the
                    // preview lands.
                    <ul className="send-list send-skel" aria-label="Reading playlists">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <li key={i} className="send-row send-skel-row">
                                <span className="send-skel-box" />
                                <div className="send-row-text">
                                    <span className="send-skel-line" />
                                    <span className="send-skel-line short" />
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : visible.length === 0 ? (
                    <div className="send-empty">No playlists{query ? ' match' : ''}.</div>
                ) : (
                    <ul className="send-list">
                        {visible.map((r) => {
                            const disabled = r.local === 0;
                            const notes = [];
                            if (r.streaming) notes.push(`${r.streaming} streaming`);
                            if (r.missing) notes.push(`${r.missing} not on this device`);
                            return (
                                <li key={r.id} className={`send-row${disabled ? ' disabled' : ''}${checked.has(r.id) ? ' checked' : ''}`} onClick={() => !disabled && toggle(r.id)}>
                                    <input type="checkbox" checked={checked.has(r.id)} disabled={disabled} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} />
                                    <div className="send-row-text">
                                        <div className="send-row-name">
                                            {r.name}
                                            {r.linkedToApp && <span className="send-badge">in {label}</span>}
                                        </div>
                                        <div className="send-row-meta">
                                            {r.local} of {r.trackCount} track{r.trackCount === 1 ? '' : 's'} will be sent{notes.length ? ` · ${notes.join(', ')}` : ''}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Modal.Body>
            <Modal.Footer>
                {app === 'rekordbox' && onExportXml && (
                    <button type="button" className="send-link send-xml" disabled={sending} onClick={() => { onClose(); onExportXml(); }}>Export XML file instead…</button>
                )}
                <div className="send-footer-summary">
                    {selected.length} playlist{selected.length === 1 ? '' : 's'} · {totals.tracks} track{totals.tracks === 1 ? '' : 's'}
                    {(totals.streaming || totals.missing) ? <span className="send-footer-skip"> · skipping {totals.streaming + totals.missing}</span> : null}
                </div>
                <Button variant="secondary" onClick={onClose} disabled={sending}>Cancel</Button>
                <Button variant="primary" onClick={send} disabled={sending || !selected.length}>
                    {sending ? <><Spinner animation="border" size="sm" /> Sending…</> : `Send to ${label}`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
